// The canvas equivalent of the native app's ChartSnapshotView + the static
// rendering half of ZoomableCandlestickChart: given candles, a visible
// range, a y-domain, drawings, and indicators, draw the whole chart into a
// 2D canvas context. Kept free of DOM/event code so the exact same
// renderer runs in the interactive chart window (HTMLCanvasElement), the
// journal capture, and the background service worker's OffscreenCanvas
// (Discord notifications) — one code path, so what gets captured/sent is
// pixel-identical to what's on screen.

import type { OHLCPoint } from "./market-data.js";
import type { ChartLine } from "./chart-lines.js";
import {
  adxSeries,
  atrSeries,
  bollingerBandSeries,
  emaSeries,
  indicatorColor,
  macdSeries,
  rsiSeries,
  simpleMovingAverage,
  stochasticSeries,
  vwapSeries,
  type IndicatorKind,
  type IndicatorPoint,
  type MACDPoint,
} from "./chart-indicators.js";

export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

// Matches the muted teal-green/coral-red palette used by TradingView —
// same RGB values as the native app's candleUpColor/candleDownColor.
// Mutable so the chart-settings menu can restyle candles; volume and the
// MACD histogram derive from these so everything stays in step.
// Two-tone GREY, not green/red: SOLID bodies, light grey up / dark grey down, so
// direction reads by LUMINANCE alone (colour-blind-safe) with no buy=green /
// sell=red emotional loading. (D-075: no hue-as-emotion; meaning by luminance.)
export const defaultChartColors = { up: "rgb(196, 196, 191)", down: "rgb(95, 94, 90)" };
let candleUpColor = defaultChartColors.up;
let candleDownColor = defaultChartColors.down;
export function setCandleColors(up: string, down: string): void {
  candleUpColor = up;
  candleDownColor = down;
}
export function getCandleColors(): { up: string; down: string } {
  return { up: candleUpColor, down: candleDownColor };
}

/** "#rrggbb" or "rgb(r, g, b)" → rgba() at the given alpha. */
export function withAlpha(color: string, alpha: number): string {
  if (color.startsWith("#") && color.length >= 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (color.startsWith("rgb(")) return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  return color;
}
export const textLabelColor = "#32ade6";

export interface ChartTheme {
  /** The tinted plot area. */
  background: string;
  /** The axis gutters (right price column, bottom time row) — kept plain. */
  surface: string;
  grid: string;
  axisLabel: string;
  /** Full-strength drawing-tool ink — must contrast with `background`
   * (white on dark, near-black on light). */
  annotation: string;
  /** The same ink at ~85% / ~55% / ~10% alpha, for outlines, fibonacci
   * levels, and area fills. */
  annotationSoft: string;
  annotationMid: string;
  annotationFaint: string;
  /** Border separating the plot from the axis gutters. */
  axisBorder: string;
}

export const darkChartTheme: ChartTheme = {
  background: "#171722", // navy plot, a hair above the #141420 page (matches tickahead.com)
  surface: "#141420",
  grid: "rgba(255,255,255,0.07)",
  axisLabel: "rgba(255,255,255,0.45)",
  annotation: "#ffffff",
  annotationSoft: "rgba(255,255,255,0.85)",
  annotationMid: "rgba(255,255,255,0.55)",
  annotationFaint: "rgba(255,255,255,0.10)",
  axisBorder: "rgba(255,255,255,0.22)",
};

export const lightChartTheme: ChartTheme = {
  background: "#f7f9fb", // soft, barely-tinted cool off-white — calm, low-glare, neutral for red/green; fixed in all lighting
  surface: "#ffffff", // axis gutters stay white
  grid: "rgba(0,0,0,0.07)",
  axisLabel: "rgba(0,0,0,0.45)",
  annotation: "#111113",
  annotationSoft: "rgba(0,0,0,0.85)",
  annotationMid: "rgba(0,0,0,0.55)",
  annotationFaint: "rgba(0,0,0,0.07)",
  axisBorder: "rgba(0,0,0,0.22)",
};

// Pages set this from their CSS variables (see theme.ts); the service
// worker has no DOM and keeps the dark default for Discord sends.
let theme: ChartTheme = darkChartTheme;
export function setChartTheme(t: ChartTheme): void {
  theme = t;
}
export function chartTheme(): ChartTheme {
  return theme;
}

export const fibonacciLevels: Array<{ fraction: number; label: string }> = [
  { fraction: 0.0, label: "0" },
  { fraction: 0.236, label: "23.6" },
  { fraction: 0.382, label: "38.2" },
  { fraction: 0.5, label: "50" },
  { fraction: 0.618, label: "61.8" },
  { fraction: 0.786, label: "78.6" },
  { fraction: 1.0, label: "100" },
];

export interface ChartScale {
  /** Plot area in canvas CSS pixels. */
  plotLeft: number;
  plotTop: number;
  plotWidth: number;
  plotHeight: number;
  timeMin: number;
  timeMax: number;
  priceMin: number;
  priceMax: number;
  xForTime(time: number): number;
  yForPrice(price: number): number;
  timeForX(x: number): number;
  priceForY(y: number): number;
  /** Bands reserved under the price plot for sub-pane indicators
   * (RSI/MACD), top to bottom. Empty when none are active. */
  subPanes: Array<{ top: number; height: number }>;
  /** Collapsed time ranges (weekend/holiday holes) — the x-axis skips these
   * so sessions sit adjacent (TradingView-style). Empty = plain linear time.
   * Kept so the renderer can mark each with a faint dashed separator. */
  gaps: Array<{ start: number; end: number }>;
}

export const Y_AXIS_WIDTH = 46;
export const X_AXIS_HEIGHT = 34;
const SUB_PANE_GAP = 5;

/** Sub-pane indicators, in their fixed stacking order under the price plot
 * (volume hugs the candles, oscillators below — the TradingView layout). */
export type SubPaneKind = "volume" | "rsi" | "stoch" | "macd" | "atr" | "adx";
export function activeSubPanes(indicators: Set<IndicatorKind>): SubPaneKind[] {
  return (["volume", "rsi", "stoch", "macd", "atr", "adx"] as SubPaneKind[]).filter((k) => indicators.has(k));
}

/** Gap-collapsed time mapping. compress(t) removes the collapsed ranges so the
 * axis skips weekends/holidays; expand(c) is its inverse. Empty gaps => identity
 * (plain linear time). Shared by makeScale AND the chart's pan/zoom math so the
 * on-screen spacing stays constant while panning (the view is driven in
 * compressed time, not real time). */
export function timeCompressor(gaps: Array<{ start: number; end: number }>): {
  compress: (t: number) => number;
  expand: (c: number) => number;
  gaps: Array<{ start: number; end: number }>;
} {
  const gapList = gaps.filter((g) => g.end > g.start).sort((a, b) => a.start - b.start);
  // Prefix sums + binary search so compress/expand stay O(log n) even when the
  // axis collapses hundreds of gaps (every weekend/session break) for even,
  // TradingView-style spacing.
  const n = gapList.length;
  const starts = new Float64Array(n);
  const ends = new Float64Array(n);
  const cumBefore = new Float64Array(n + 1); // total collapsed length before gap i
  for (let i = 0; i < n; i++) {
    starts[i] = gapList[i]!.start;
    ends[i] = gapList[i]!.end;
    cumBefore[i + 1] = cumBefore[i]! + (ends[i]! - starts[i]!);
  }
  const compress = (t: number): number => {
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (ends[mid]! <= t) lo = mid + 1;
      else hi = mid;
    }
    let off = cumBefore[lo]!; // gaps fully before t
    if (lo < n && starts[lo]! < t) off += t - starts[lo]!; // t inside a gap
    return t - off;
  };
  const expand = (c: number): number => {
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid]! - cumBefore[mid]! <= c) lo = mid + 1;
      else hi = mid;
    }
    return c + cumBefore[lo]!;
  };
  return { compress, expand, gaps: gapList };
}

export function makeScale(
  width: number,
  height: number,
  timeMin: number,
  timeMax: number,
  priceMin: number,
  priceMax: number,
  subPaneCount = 0,
  gaps: Array<{ start: number; end: number }> = []
): ChartScale {
  const plotLeft = 0;
  const plotTop = 4;
  const plotWidth = Math.max(1, width - Y_AXIS_WIDTH);
  // Sub-panes shave off the bottom of the price plot: each gets ~16% of
  // the surface, clamped so tiny 4-up panes stay readable and huge
  // windows don't drown the candles.
  const paneHeight = Math.max(34, Math.min(84, (height - X_AXIS_HEIGHT) * 0.16));
  const reserved = subPaneCount * (paneHeight + SUB_PANE_GAP);
  const plotHeight = Math.max(1, height - X_AXIS_HEIGHT - plotTop - reserved);
  const subPanes = Array.from({ length: subPaneCount }, (_, i) => ({
    top: plotTop + plotHeight + SUB_PANE_GAP + i * (paneHeight + SUB_PANE_GAP),
    height: paneHeight,
  }));
  const priceSpan = Math.max(1e-9, priceMax - priceMin);
  const { compress, expand, gaps: gapList } = timeCompressor(gaps);
  const cMin = compress(timeMin);
  const cSpan = Math.max(1, compress(timeMax) - cMin);
  return {
    plotLeft,
    plotTop,
    plotWidth,
    plotHeight,
    timeMin,
    timeMax,
    priceMin,
    priceMax,
    subPanes,
    gaps: gapList,
    xForTime: (time) => plotLeft + ((compress(time) - cMin) / cSpan) * plotWidth,
    yForPrice: (price) => plotTop + (1 - (price - priceMin) / priceSpan) * plotHeight,
    timeForX: (x) => expand(cMin + ((x - plotLeft) / plotWidth) * cSpan),
    priceForY: (y) => priceMin + (1 - (y - plotTop) / plotHeight) * priceSpan,
  };
}

/** Where the time-axis strip begins: under the last sub-pane, or under
 * the price plot when there are none. */
function timeAxisTop(scale: ChartScale): number {
  const last = scale.subPanes[scale.subPanes.length - 1];
  return last ? last.top + last.height : scale.plotTop + scale.plotHeight;
}

/**
 * Candle body width sized off the smallest actual gap between any two
 * adjacent candles, not the average — the average lets adjacent weekday
 * candles overlap when weekends leave irregular holes in the date axis
 * (the exact bug the native app hit with .ratio()/average-based sizing).
 */
export function candleWidth(candles: OHLCPoint[], scale: ChartScale): number {
  if (candles.length < 2) return 4;
  // On-screen pixel spacing between adjacent candles, measured THROUGH the scale
  // so it is correct on a gap-collapsed axis too (weekends leave no wide hole).
  // Median (not min) keeps a stray tiny spacing from shrinking every candle.
  const dxs: number[] = [];
  for (let i = 1; i < candles.length; i++) {
    const dx = scale.xForTime(candles[i]!.date.getTime()) - scale.xForTime(candles[i - 1]!.date.getTime());
    if (dx > 0.01) dxs.push(dx);
  }
  if (!dxs.length) return 4;
  dxs.sort((a, b) => a - b);
  const med = dxs[Math.floor(dxs.length / 2)]!;
  // Body ~80% of the slot, ~20% gap — the dense TradingView look (was 60%).
  return Math.max(med * 0.82, 1.5);
}

/**
 * Expands a near-zero open/close body to a small minimum height
 * (proportional to the visible price range) so doji candles keep a visible
 * body instead of collapsing into the wick. Ported from candleBodyBounds.
 */
export function candleBodyBounds(
  open: number,
  close: number,
  priceMin: number,
  priceMax: number
): { open: number; close: number } {
  const minHeight = (priceMax - priceMin) * 0.006;
  if (Math.abs(close - open) >= minHeight) return { open, close };
  const mid = (open + close) / 2;
  const half = minHeight / 2;
  return close >= open ? { open: mid - half, close: mid + half } : { open: mid + half, close: mid - half };
}

export interface RenderOptions {
  candles: OHLCPoint[];
  isIntraday: boolean;
  lines: ChartLine[];
  activeIndicators: Set<IndicatorKind>;
  width: number;
  height: number;
  background?: string;
  /** Trend lines whose alarm is armed (horizontal ones live in
   * watchlistAlerts, so the caller resolves them). */
  armedLineIDs?: Set<string>;
}

function drawIndicatorLine(ctx: Ctx2D, points: IndicatorPoint[], scale: ChartScale, color: string, dash: number[] = []) {
  if (points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.setLineDash(dash);
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = scale.xForTime(p.time);
    const y = scale.yForPrice(p.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.restore();
}

function niceTicks(min: number, max: number, count: number): number[] {
  const span = max - min;
  if (span <= 0) return [min];
  const rawStep = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const residual = rawStep / magnitude;
  const step = (residual >= 5 ? 5 : residual >= 2 ? 2 : 1) * magnitude;
  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) ticks.push(v);
  return ticks;
}

// The x-axis renders in a user-chosen time zone (undefined = the browser's local
// zone). Rebuilt whenever the zone changes via setAxisTimeZone().
// Two label formatters do all the work: a 24-hour clock (HH:MM) for anything
// inside a day, and a short month name; day and year labels are bare numbers
// pulled from the wall-clock parts. See timeAxisTicks() for the labelling rule.
let axisTimeZone: string | undefined;
let axisHM: Intl.DateTimeFormat, axisMonth: Intl.DateTimeFormat, axisParts: Intl.DateTimeFormat;
function buildAxisFormatters(tz?: string): void {
  const z = tz ? { timeZone: tz } : {};
  axisHM = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, ...z });
  axisMonth = new Intl.DateTimeFormat("en-US", { month: "short", ...z });
  // Full wall-clock parts in the axis zone — for snapping the vertical grid to
  // real minute/hour/day/month/year boundaries and reading each tick's weight.
  axisParts = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short", hour12: false, ...z });
}
buildAxisFormatters();
/** Set the time zone used for x-axis labels (IANA name, e.g. "UTC"; undefined = local). */
export function setAxisTimeZone(tz?: string): void { axisTimeZone = tz || undefined; buildAxisFormatters(axisTimeZone); }
/** The active axis time zone (undefined = local). */
export function getAxisTimeZone(): string | undefined { return axisTimeZone; }

// --- Zone-correct BAR-ANCHORED time axis (D-093, reworked D-126) ------------
// The marks only ever sit on real bars, evenly spaced on screen (like a trading
// terminal). Each bar is labelled by the coarsest calendar boundary it OPENS vs
// the previous bar — its "weight":
//   inside a day  -> HH:MM (24-hour)
//   new day       -> bare day-of-month number  ("18")   [at the bar that opens it]
//   new month     -> month name                ("Aug")
//   new year      -> year                       ("2026")
// The coarser a mark, the bolder — and marks are picked greedily by weight at a
// constant pixel pitch. Because they ride real bars, weekends/session breaks can
// never cram or leave a hole: the day number simply jumps while spacing stays
// even. See timeAxisMarks(). Uses only the axis time zone + the xForTime mapping.
const AXIS_WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
type GUnit = "minute" | "hour" | "day" | "week" | "month" | "year";
const UNIT_RANK: Record<GUnit, number> = { minute: 0, hour: 1, day: 2, week: 2, month: 3, year: 4 };
interface ZParts { year: number; month: number; day: number; hour: number; minute: number; weekday: number; }
function zoneParts(t: number): ZParts {
  const p = axisParts.formatToParts(new Date(t));
  const g = (ty: string): string => p.find((x) => x.type === ty)?.value ?? "0";
  return { year: +g("year"), month: +g("month"), day: +g("day"), hour: (+g("hour")) % 24, minute: +g("minute"), weekday: AXIS_WD[g("weekday")] ?? 0 };
}
function tickLabel(t: number, w: GUnit): string {
  if (w === "year") return String(zoneParts(t).year);
  if (w === "month") return axisMonth.format(new Date(t));
  if (w === "day" || w === "week") return String(zoneParts(t).day);
  return axisHM.format(new Date(t)); // hour / minute → 24-hour HH:MM
}
/** The axis time zone's UTC offset (ms) near `t`, from ONE Intl call. Adding it to
 * a timestamp lets us read wall-clock fields with plain getUTC* — no per-bar Intl,
 * which keeps the per-frame axis cheap (weights over hundreds of bars). */
function zoneOffsetMs(t: number): number {
  const c = zoneParts(t);
  return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute) - Math.floor(t / 60_000) * 60_000;
}

/** One chosen axis mark: a real bar, its x, its adaptive label, and whether it's
 *  a bolded boundary. */
export interface AxisMark { time: number; x: number; label: string; bold: boolean; }
const UNIT_OF_RANK: GUnit[] = ["minute", "hour", "day", "month", "year"];
/** Even-spaced, BAR-ANCHORED axis marks — the way a trading terminal does it.
 *  Marks only ever land on real bars (so weekends/session breaks can never cram
 *  or leave a hole), each bar carries the weight of the coarsest calendar boundary
 *  it opens vs the previous bar, and we pick marks greedily by weight at a constant
 *  pixel pitch. Labels: HH:MM intraday, the day number at a day open, the month
 *  name at a month open, the year at a year open. Bold = coarser than the finest
 *  unit on screen (or day-and-up on daily/weekly charts). Pure given the axis zone
 *  + the xForTime mapping, so it is unit-tested directly. */
export function timeAxisMarks(
  barTimes: number[],
  xForTime: (t: number) => number,
  left: number,
  right: number,
  pitch: number,
  opts: { dayIsMajor?: boolean } = {},
): AxisMark[] {
  const n = barTimes.length;
  if (n < 2) return [];
  // The bar's weight = the coarsest calendar boundary it OPENS vs the previous bar.
  // Decode wall-clock fields via a single cached zone offset (fast: no Intl per
  // bar). The offset is sampled mid-range and assumed constant across the view —
  // a DST shift would only misweight one bar, which is visually irrelevant. The
  // first bar uses an extrapolated predecessor so it gets its true boundary rank.
  const iv0 = barTimes[1]! - barTimes[0]!;
  let off = 0;
  const wf = (t: number): { y: number; mo: number; d: number; h: number } => {
    const d = new Date(t + off);
    return { y: d.getUTCFullYear(), mo: d.getUTCMonth(), d: d.getUTCDate(), h: d.getUTCHours() };
  };
  const rankAt = (i: number): number => {
    const c = wf(barTimes[i]!);
    const p = wf(i > 0 ? barTimes[i - 1]! : barTimes[0]! - iv0);
    if (p.y !== c.y) return 4;
    if (p.mo !== c.mo) return 3;
    if (p.d !== c.d) return 2;
    if (p.h !== c.h) return 1;
    return 0;
  };
  const cands: Array<{ time: number; x: number; r: number }> = [];
  for (let i = 0; i < n; i++) {
    const x = xForTime(barTimes[i]!);
    if (x < left || x > right) continue;
    if (!cands.length) off = zoneOffsetMs(barTimes[i]!); // sample the zone offset from the first in-view bar
    cands.push({ time: barTimes[i]!, x, r: rankAt(i) });
  }
  if (!cands.length) return [];
  // Pick the DISPLAY granularity so we never show a unit finer than the zoom wants
  // (e.g. no stray "02:30" at a day zoom). The coarser landmark unit must be sparse
  // (≥ ~2 pitches apart); the base unit below it is the one we subsample to pitch.
  const medSpacing: number[] = [];
  for (let r = 0; r <= 4; r++) {
    const xs = cands.filter((c) => c.r >= r).map((c) => c.x).sort((a, b) => a - b);
    if (xs.length < 2) { medSpacing[r] = Infinity; continue; }
    const g: number[] = [];
    for (let i = 1; i < xs.length; i++) g.push(xs[i]! - xs[i - 1]!);
    g.sort((a, b) => a - b);
    medSpacing[r] = g[g.length >> 1]!;
  }
  // baseRank = the finest unit we're allowed to label at this zoom.
  //  • If the bars themselves are ≥ a pitch apart (very zoomed in), show every bar.
  //  • Otherwise only drop to a sub-day unit when the DAY is wide enough to hold
  //    intraday marks (its coarser landmark ≥ 3.5 pitches apart) — so a multi-day
  //    stock/gold view shows day numbers only (even boxes, no stray "02:00"),
  //    while a 1–2 day view opens up to hourly marks.
  let baseRank: number;
  if (medSpacing[0]! >= pitch) {
    baseRank = 0;
  } else {
    baseRank = 4;
    for (let u = 1; u <= 4; u++) {
      const coarser = u < 4 ? medSpacing[u + 1]! : Infinity;
      if (coarser >= pitch * 6) { baseRank = u; break; }
    }
  }
  // Highest weight first so a month/day boundary always wins its slot; each kept
  // mark clears `pitch` px of every other and the time-zone pill / price gutter.
  const placed: number[] = [];
  const chosen: Array<{ time: number; x: number; r: number }> = [];
  for (const m of cands.filter((c) => c.r >= baseRank).sort((a, b) => b.r - a.r || a.x - b.x)) {
    if (m.x - left < 78 || m.x > right - 22) continue;
    let ok = true;
    for (const px of placed) if (Math.abs(px - m.x) < pitch) { ok = false; break; }
    if (!ok) continue;
    placed.push(m.x);
    chosen.push(m);
  }
  chosen.sort((a, b) => a.x - b.x);
  return chosen.map((m) => ({
    time: m.time,
    x: m.x,
    label: tickLabel(m.time, UNIT_OF_RANK[m.r]!),
    bold: opts.dayIsMajor ? m.r >= UNIT_RANK.day : m.r > baseRank,
  }));
}

function drawAxes(ctx: Ctx2D, scale: ChartScale, isIntraday: boolean, barTimes: number[]) {
  ctx.save();
  ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";

  // Y (price) axis: right-side labels + horizontal grid lines.
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  for (const price of niceTicks(scale.priceMin, scale.priceMax, 4)) {
    const y = scale.yForPrice(price);
    ctx.strokeStyle = theme.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(scale.plotLeft, y);
    ctx.lineTo(scale.plotLeft + scale.plotWidth, y);
    ctx.stroke();
    ctx.fillStyle = theme.axisLabel;
    const label = price >= 1000 ? price.toFixed(0) : price >= 10 ? price.toFixed(1) : price.toFixed(2);
    ctx.fillText(label, scale.plotLeft + scale.plotWidth + 4, y);
  }

  // X (time) axis: BAR-ANCHORED, evenly spaced (like a trading terminal). Marks
  // only ever sit on real bars, so weekends/session breaks can never cram or leave
  // a hole; each is labelled by the coarsest boundary that bar opens (HH:MM / day /
  // month / year), bolder for the coarser ones. Grid lines run through any
  // sub-panes so time stays visually continuous.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const axisTop = timeAxisTop(scale);
  const left = scale.plotLeft, right = scale.plotLeft + scale.plotWidth;
  // Project bars into the empty space to the right of the last candle so the future
  // area still gets time labels (instead of going blank). Step by the median recent
  // bar interval; the labels are synthetic, never drawn as candles.
  let axisBars = barTimes;
  if (barTimes.length >= 2) {
    const last = barTimes[barTimes.length - 1]!;
    const deltas: number[] = [];
    for (let i = Math.max(1, barTimes.length - 60); i < barTimes.length; i++) deltas.push(barTimes[i]! - barTimes[i - 1]!);
    deltas.sort((a, b) => a - b);
    const iv = deltas[deltas.length >> 1] || (last - barTimes[barTimes.length - 2]!);
    if (iv > 0 && scale.xForTime(last) < right) {
      axisBars = barTimes.slice();
      let t = last + iv, guard = 0;
      // Cap projected bars to roughly what fits the empty space (never a spin).
      const cap = Math.min(1000, Math.ceil(scale.plotWidth / Math.max(1, scale.xForTime(last + iv) - scale.xForTime(last))) + 4);
      while (scale.xForTime(t) <= right && guard++ < cap) { axisBars.push(t); t += iv; }
    }
  }
  const marks = timeAxisMarks(axisBars, (t) => scale.xForTime(t), left, right, 100, { dayIsMajor: !isIntraday });
  const tickXs: number[] = [];
  for (const m of marks) {
    tickXs.push(m.x);
    ctx.strokeStyle = theme.grid;
    ctx.globalAlpha = m.bold ? 0.9 : 0.45;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m.x, scale.plotTop);
    ctx.lineTo(m.x, axisTop);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.fillStyle = theme.axisLabel;
    ctx.font = `${m.bold ? 700 : 600} 10px -apple-system, 'Segoe UI', sans-serif`;
    ctx.fillText(m.label, m.x, axisTop + X_AXIS_HEIGHT / 2);
  }

  // Faint dashed separators where the axis collapsed a weekend/holiday gap. These
  // are pinned to the tick lattice: a seam only draws when its boundary coincides
  // with a gridline the axis is ALREADY showing at this zoom (within a couple px).
  // So the grid boxes stay a consistent size, and a seam appears only once you're
  // zoomed in far enough that that time would naturally be a gridline — never as a
  // stray line floating between two labels. Intraday only (that's where the
  // weekend collapse is meaningful).
  if (isIntraday && scale.gaps.length && tickXs.length) {
    ctx.save();
    ctx.strokeStyle = theme.grid;
    ctx.globalAlpha = 0.55;
    ctx.setLineDash([3, 3]);
    const WEEKEND_MS = 40 * 3600_000; // only weekend/holiday-scale holes qualify — daily session breaks are marked elsewhere
    const ON_GRID_PX = 2.5; // how close a seam must sit to a real gridline to be shown
    let lastGx = -Infinity;
    for (const g of scale.gaps) {
      if (g.end - g.start < WEEKEND_MS) continue;
      const gx = scale.xForTime(g.start);
      if (gx < scale.plotLeft || gx > scale.plotLeft + scale.plotWidth) continue;
      // Skip unless this seam lands on the natural grid at the current zoom.
      let onGrid = false;
      for (const tx of tickXs) if (Math.abs(tx - gx) <= ON_GRID_PX) { onGrid = true; break; }
      if (!onGrid) continue;
      if (gx - lastGx < 6) continue; // collapse near-duplicates into one line
      lastGx = gx;
      ctx.beginPath();
      ctx.moveTo(gx, scale.plotTop);
      ctx.lineTo(gx, axisTop);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
  }

  // Demarcate the axis gutters from the plot, TradingView-style — also
  // marks the zones where scrolling zooms a single axis.
  ctx.strokeStyle = theme.axisBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  const axisX = scale.plotLeft + scale.plotWidth + 0.5;
  const axisY = axisTop + 0.5;
  ctx.moveTo(axisX, 0);
  ctx.lineTo(axisX, axisY);
  ctx.moveTo(scale.plotLeft, axisY);
  ctx.lineTo(axisX, axisY); // stop at the divider — don't run on across the price-axis corner
  ctx.stroke();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Sub-pane indicators (RSI / MACD) — bands under the price plot with their
// own y-scales, sharing the price plot's time axis.

interface SubPaneRect {
  top: number;
  height: number;
}

/** Frame + name label shared by every sub-pane; returns the content rect. */
function drawSubPaneFrame(ctx: Ctx2D, scale: ChartScale, rect: SubPaneRect, label: string) {
  ctx.strokeStyle = theme.axisBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(scale.plotLeft, rect.top - 2.5);
  ctx.lineTo(scale.plotLeft + scale.plotWidth + Y_AXIS_WIDTH, rect.top - 2.5);
  ctx.stroke();
  ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = theme.axisLabel;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(label, scale.plotLeft + 4, rect.top + 2);
}

/** Right-gutter value label for a sub-pane, in the indicator's color. */
function drawSubPaneValue(ctx: Ctx2D, scale: ChartScale, y: number, text: string, color: string) {
  ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, scale.plotLeft + scale.plotWidth + 4, y);
}

/** Compact volume label: 1.2K / 3.4M / 5.6B. */
function fmtVolume(v: number): string {
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

function drawVolumePane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  drawSubPaneFrame(ctx, scale, rect, "Volume");
  // Only bars actually on screen set the y-domain — one giant historic
  // print shouldn't flatten today's session.
  const visible = candles.filter((c) => {
    const t = c.date.getTime();
    return t >= scale.timeMin && t <= scale.timeMax;
  });
  const maxVolume = visible.reduce((m, c) => Math.max(m, c.volume), 0);
  if (maxVolume <= 0) {
    ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
    ctx.fillStyle = theme.axisLabel;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("No volume reported for this instrument", scale.plotLeft + scale.plotWidth / 2, rect.top + rect.height / 2);
    return;
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, rect.top, scale.plotWidth, rect.height);
  ctx.clip();
  const barWidth = Math.max(1, candleWidth(candles, scale) * 0.8);
  const bottom = rect.top + rect.height;
  for (const c of visible) {
    if (c.volume <= 0) continue;
    const x = scale.xForTime(c.date.getTime());
    const h = Math.max(1, (c.volume / maxVolume) * (rect.height - 12));
    ctx.fillStyle = c.close >= c.open ? withAlpha(candleUpColor, 0.5) : withAlpha(candleDownColor, 0.5);
    ctx.fillRect(x - barWidth / 2, bottom - h, barWidth, h);
  }
  ctx.restore();

  const last = visible[visible.length - 1];
  if (last && last.volume > 0) {
    const y = bottom - Math.max(1, (last.volume / maxVolume) * (rect.height - 12));
    drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, y), fmtVolume(last.volume), theme.axisLabel);
  }
}

function drawRSIPane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  const points = rsiSeries(candles);
  drawSubPaneFrame(ctx, scale, rect, "RSI 14");
  if (!points.length) return;
  const yFor = (v: number) => rect.top + (1 - v / 100) * rect.height;

  // 30/70 guides — the zone traders actually read.
  ctx.strokeStyle = theme.grid;
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  for (const level of [30, 70]) {
    ctx.beginPath();
    ctx.moveTo(scale.plotLeft, yFor(level));
    ctx.lineTo(scale.plotLeft + scale.plotWidth, yFor(level));
    ctx.stroke();
    ctx.fillStyle = theme.axisLabel;
    ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(String(level), scale.plotLeft + scale.plotWidth + 4, yFor(level));
  }
  ctx.setLineDash([]);

  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, rect.top, scale.plotWidth, rect.height);
  ctx.clip();
  ctx.strokeStyle = indicatorColor.rsi;
  ctx.lineWidth = 1.2;
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = scale.xForTime(p.time);
    const y = yFor(p.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.restore();

  const last = points[points.length - 1]!;
  drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, Math.min(rect.top + rect.height - 4, yFor(last.value))), last.value.toFixed(0), indicatorColor.rsi);
}

function drawATRPane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  const points = atrSeries(candles);
  drawSubPaneFrame(ctx, scale, rect, "ATR 14");
  if (!points.length) return;
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    min = Math.min(min, p.value);
    max = Math.max(max, p.value);
  }
  const pad = Math.max((max - min) * 0.15, 1e-9);
  const yFor = (v: number) => rect.top + (1 - (v - (min - pad)) / (max - min + 2 * pad)) * rect.height;

  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, rect.top, scale.plotWidth, rect.height);
  ctx.clip();
  ctx.strokeStyle = indicatorColor.atr;
  ctx.lineWidth = 1.2;
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = scale.xForTime(p.time);
    const y = yFor(p.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.restore();

  const last = points[points.length - 1]!;
  const label = last.value >= 100 ? last.value.toFixed(0) : last.value.toFixed(2);
  drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, Math.min(rect.top + rect.height - 4, yFor(last.value))), label, indicatorColor.atr);
}

function drawBoundedLine(ctx: Ctx2D, points: Array<{ time: number; value: number }>, scale: ChartScale, rect: SubPaneRect, color: string, yFor: (v: number) => number) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, rect.top, scale.plotWidth, rect.height);
  ctx.clip();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = scale.xForTime(p.time);
    const y = yFor(p.value);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.restore();
}

function drawStochPane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  const points = stochasticSeries(candles);
  drawSubPaneFrame(ctx, scale, rect, "Stochastic 14·3");
  if (!points.length) return;
  const yFor = (v: number) => rect.top + (1 - v / 100) * rect.height;
  ctx.strokeStyle = theme.grid;
  ctx.setLineDash([3, 3]);
  for (const level of [20, 80]) {
    ctx.beginPath();
    ctx.moveTo(scale.plotLeft, yFor(level));
    ctx.lineTo(scale.plotLeft + scale.plotWidth, yFor(level));
    ctx.stroke();
  }
  ctx.setLineDash([]);
  drawBoundedLine(ctx, points.map((p) => ({ time: p.time, value: p.k })), scale, rect, indicatorColor.stoch, yFor);
  drawBoundedLine(ctx, points.map((p) => ({ time: p.time, value: p.d })), scale, rect, "#ff9f0a", yFor);
  const last = points[points.length - 1]!;
  drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, Math.min(rect.top + rect.height - 4, yFor(last.k))), last.k.toFixed(0), indicatorColor.stoch);
}

function drawADXPane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  const points = adxSeries(candles);
  drawSubPaneFrame(ctx, scale, rect, "ADX 14");
  if (!points.length) return;
  const yFor = (v: number) => rect.top + (1 - Math.min(v, 100) / 100) * rect.height;
  // The 25 line: above it a trend is generally considered "strong".
  ctx.strokeStyle = theme.grid;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(scale.plotLeft, yFor(25));
  ctx.lineTo(scale.plotLeft + scale.plotWidth, yFor(25));
  ctx.stroke();
  ctx.setLineDash([]);
  drawBoundedLine(ctx, points, scale, rect, indicatorColor.adx, yFor);
  const last = points[points.length - 1]!;
  drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, Math.min(rect.top + rect.height - 4, yFor(last.value))), last.value.toFixed(0), indicatorColor.adx);
}

function drawMACDPane(ctx: Ctx2D, candles: OHLCPoint[], scale: ChartScale, rect: SubPaneRect) {
  const points = macdSeries(candles);
  drawSubPaneFrame(ctx, scale, rect, "MACD 12·26·9");
  if (!points.length) return;
  let maxAbs = 1e-9;
  for (const p of points) maxAbs = Math.max(maxAbs, Math.abs(p.macd), Math.abs(p.signal), Math.abs(p.histogram));
  // Symmetric domain around zero so the zero line sits mid-pane.
  const yFor = (v: number) => rect.top + (1 - (v + maxAbs) / (2 * maxAbs)) * rect.height;

  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, rect.top, scale.plotWidth, rect.height);
  ctx.clip();

  // Zero line.
  ctx.strokeStyle = theme.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(scale.plotLeft, yFor(0));
  ctx.lineTo(scale.plotLeft + scale.plotWidth, yFor(0));
  ctx.stroke();

  // Histogram first so the lines read on top.
  const barWidth = Math.max(1, candleWidth(candles, scale) * 0.7);
  for (const p of points) {
    const x = scale.xForTime(p.time);
    const y = yFor(p.histogram);
    const y0 = yFor(0);
    ctx.fillStyle = p.histogram >= 0 ? withAlpha(candleUpColor, 0.55) : withAlpha(candleDownColor, 0.55);
    ctx.fillRect(x - barWidth / 2, Math.min(y, y0), barWidth, Math.max(Math.abs(y - y0), 1));
  }

  const drawSeries = (value: (p: MACDPoint) => number, color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.lineJoin = "round";
    ctx.beginPath();
    points.forEach((p, i) => {
      const x = scale.xForTime(p.time);
      const y = yFor(value(p));
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  };
  drawSeries((p) => p.macd, indicatorColor.macd);
  drawSeries((p) => p.signal, "#ff9f0a");
  ctx.restore();

  const last = points[points.length - 1]!;
  drawSubPaneValue(ctx, scale, Math.max(rect.top + 4, Math.min(rect.top + rect.height - 4, yFor(last.macd))), last.macd.toFixed(2), indicatorColor.macd);
}

function drawArrowhead(ctx: Ctx2D, x1: number, y1: number, x2: number, y2: number, length = 7, width = 5) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.max(Math.hypot(dx, dy), 0.001);
  const ux = dx / dist;
  const uy = dy / dist;
  const px = -uy;
  const py = ux;
  const backX = x2 - ux * length;
  const backY = y2 - uy * length;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(backX + px * width, backY + py * width);
  ctx.lineTo(backX - px * width, backY - py * width);
  ctx.closePath();
  ctx.fill();
}

export function drawLine(ctx: Ctx2D, line: ChartLine, scale: ChartScale, armed = false) {
  const x1 = scale.xForTime(line.start.time);
  const y1 = scale.yForPrice(line.start.price);
  const x2 = scale.xForTime(line.end.time);
  const y2 = scale.yForPrice(line.end.price);
  ctx.save();

  switch (line.kind) {
    case "text": {
      ctx.font = "600 9px -apple-system, 'Segoe UI', sans-serif";
      ctx.fillStyle = textLabelColor;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(line.text ?? "", x1, y1);
      break;
    }
    case "arrow": {
      ctx.strokeStyle = theme.annotation;
      ctx.fillStyle = theme.annotation;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      drawArrowhead(ctx, x1, y1, x2, y2);
      break;
    }
    case "rectangle": {
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);
      ctx.fillStyle = theme.annotationFaint;
      ctx.fillRect(left, top, w, h);
      ctx.strokeStyle = theme.annotationSoft;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(left, top, w, h);
      break;
    }
    case "fibonacci": {
      ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      for (const level of fibonacciLevels) {
        const price = line.start.price + (line.end.price - line.start.price) * level.fraction;
        const y = scale.yForPrice(price);
        ctx.strokeStyle = theme.annotationMid;
        ctx.lineWidth = level.fraction === 0 || level.fraction === 1 ? 1.5 : 1;
        ctx.beginPath();
        ctx.moveTo(scale.plotLeft, y);
        ctx.lineTo(scale.plotLeft + scale.plotWidth, y);
        ctx.stroke();
        ctx.fillStyle = theme.annotationMid;
        ctx.fillText(`${level.label}%`, scale.plotLeft + 2, y - 1);
      }
      break;
    }
    case "freeform": {
      const strokes = line.strokes ?? [line.path ?? [line.start, line.end]];
      ctx.strokeStyle = theme.annotation;
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (const points of strokes) {
        if (points.length < 2) continue;
        ctx.beginPath();
        points.forEach((p, i) => {
          const x = scale.xForTime(p.time);
          const y = scale.yForPrice(p.price);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
      }
      break;
    }
    case "longPosition":
    case "shortPosition": {
      if (line.stopLossPrice == null) break;
      const stopY = scale.yForPrice(line.stopLossPrice);
      const isLong = line.kind === "longPosition";
      const entryY = y1;
      const targetY = y2;
      const boxLeft = Math.min(x1, x2);
      const boxRight = Math.max(x1, x2);
      const boxWidth = Math.max(boxRight - boxLeft, 20);
      const risk = Math.abs(line.start.price - line.stopLossPrice);
      const reward = Math.abs(line.end.price - line.start.price);
      const riskReward = risk > 0 ? reward / risk : 0;

      ctx.fillStyle = "rgba(48,209,88,0.16)";
      ctx.fillRect(boxLeft, Math.min(entryY, targetY), boxWidth, Math.max(Math.abs(targetY - entryY), 1));
      ctx.fillStyle = "rgba(255,69,58,0.16)";
      ctx.fillRect(boxLeft, Math.min(entryY, stopY), boxWidth, Math.max(Math.abs(stopY - entryY), 1));

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(boxLeft, entryY);
      ctx.lineTo(boxLeft + boxWidth, entryY);
      ctx.stroke();

      ctx.lineWidth = 1;
      ctx.setLineDash([3, 2]);
      ctx.strokeStyle = "#30d158";
      ctx.beginPath();
      ctx.moveTo(boxLeft, targetY);
      ctx.lineTo(boxLeft + boxWidth, targetY);
      ctx.stroke();
      ctx.strokeStyle = "#ff453a";
      ctx.beginPath();
      ctx.moveTo(boxLeft, stopY);
      ctx.lineTo(boxLeft + boxWidth, stopY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Info label chip.
      const label1 = isLong ? "LONG" : "SHORT";
      const label2 = `R:R ${riskReward.toFixed(1)}`;
      const label3 = `+$${reward.toFixed(2)}  −$${risk.toFixed(2)}`;
      ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
      const chipWidth = Math.max(ctx.measureText(label3).width, ctx.measureText(label2).width, 30) + 6;
      const chipX = boxLeft + 2;
      const chipY = Math.max(2, Math.min(entryY, targetY, stopY) - 32);
      ctx.fillStyle = "rgba(0,0,0,0.65)";
      ctx.fillRect(chipX, chipY, chipWidth, 30);
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = "bold 8px -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText(label1, chipX + 3, chipY + 3);
      ctx.font = "8px -apple-system, 'Segoe UI', sans-serif";
      ctx.fillText(label2, chipX + 3, chipY + 12);
      ctx.fillText(label3, chipX + 3, chipY + 21);
      break;
    }
    default: {
      // trend — alarm-armed lines show yellow so the armed state is
      // visible at a glance.
      ctx.strokeStyle = armed ? "#ffd60a" : theme.annotation;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 3]);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
  ctx.restore();
}

/** Renders the full chart. Returns the scale used, for callers that need hit-testing. */
export function renderChart(ctx: Ctx2D, options: RenderOptions, scaleOverride?: ChartScale): ChartScale | null {
  const { candles, isIntraday, lines, activeIndicators, width, height } = options;

  // Axis gutters (right price column, bottom time row) stay plain; only the plot column is tinted.
  ctx.fillStyle = theme.surface;
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = options.background ?? theme.background;
  ctx.fillRect(0, 0, Math.max(0, width - Y_AXIS_WIDTH), Math.max(0, height - X_AXIS_HEIGHT));
  if (candles.length < 2) return null;

  let scale = scaleOverride ?? null;
  if (!scale) {
    const first = candles[0]!.date.getTime();
    const last = candles[candles.length - 1]!.date.getTime();
    let minLow = Infinity;
    let maxHigh = -Infinity;
    for (const c of candles) {
      minLow = Math.min(minLow, c.low);
      maxHigh = Math.max(maxHigh, c.high);
    }
    const padding = (maxHigh - minLow) * 0.05;
    scale = makeScale(width, height, first, last, minLow - padding, maxHigh + padding, activeSubPanes(activeIndicators).length);
  }

  drawAxes(ctx, scale, isIntraday, candles.map((c) => c.date.getTime()));

  // Clip candle/indicator drawing to the plot area so panning/zooming
  // doesn't spill marks over the axis gutters.
  ctx.save();
  ctx.beginPath();
  ctx.rect(scale.plotLeft, 0, scale.plotWidth, scale.plotTop + scale.plotHeight + 1);
  ctx.clip();

  const bodyWidth = candleWidth(candles, scale);
  for (const candle of candles) {
    const time = candle.date.getTime();
    if (time < scale.timeMin - 86400_000 || time > scale.timeMax + 86400_000) continue;
    const x = scale.xForTime(time);
    const color = candle.close >= candle.open ? candleUpColor : candleDownColor;

    // Two-tone grey: SOLID bodies (light grey up, dark grey down) so direction
    // reads by luminance alone — colour-blind-safe, no green/red. Wicks are a
    // uniform thin mid-grey for both directions.
    ctx.strokeStyle = "rgb(138, 138, 142)";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(x, scale.yForPrice(candle.low));
    ctx.lineTo(x, scale.yForPrice(candle.high));
    ctx.stroke();

    const body = candleBodyBounds(candle.open, candle.close, scale.priceMin, scale.priceMax);
    const yOpen = scale.yForPrice(body.open);
    const yClose = scale.yForPrice(body.close);
    const bx = x - bodyWidth / 2;
    const by = Math.min(yOpen, yClose);
    const bh = Math.max(Math.abs(yClose - yOpen), 1);
    ctx.fillStyle = color;
    ctx.fillRect(bx, by, bodyWidth, bh);
    // Light up-bodies get a slightly darker outline so they read against the pale
    // plot background; dark down-bodies define themselves (border = fill).
    ctx.strokeStyle = candle.close >= candle.open ? "rgb(150, 150, 145)" : color;
    ctx.lineWidth = 1;
    ctx.strokeRect(bx + 0.5, by + 0.5, Math.max(bodyWidth - 1, 1), Math.max(bh - 1, 1));
  }

  if (activeIndicators.has("sma20")) drawIndicatorLine(ctx, simpleMovingAverage(candles, 20), scale, indicatorColor.sma20);
  if (activeIndicators.has("sma50")) drawIndicatorLine(ctx, simpleMovingAverage(candles, 50), scale, indicatorColor.sma50);
  if (activeIndicators.has("sma200")) drawIndicatorLine(ctx, simpleMovingAverage(candles, 200), scale, indicatorColor.sma200);
  if (activeIndicators.has("ema20")) drawIndicatorLine(ctx, emaSeries(candles, 20), scale, indicatorColor.ema20);
  if (activeIndicators.has("ema50")) drawIndicatorLine(ctx, emaSeries(candles, 50), scale, indicatorColor.ema50);
  if (activeIndicators.has("vwap")) {
    drawIndicatorLine(ctx, vwapSeries(candles, isIntraday), scale, indicatorColor.vwap, [4, 2]);
  }
  if (activeIndicators.has("bollingerBands")) {
    const band = bollingerBandSeries(candles);
    const color = indicatorColor.bollingerBands + "b3"; // ~0.7 alpha
    drawIndicatorLine(ctx, band.map((p) => ({ time: p.time, value: p.upper })), scale, color);
    drawIndicatorLine(ctx, band.map((p) => ({ time: p.time, value: p.lower })), scale, color);
  }

  for (const line of lines) {
    drawLine(ctx, line, scale, line.alarmEnabled || options.armedLineIDs?.has(line.id));
  }

  // Live-price marker: a dotted line across the plot at the latest close,
  // green while the last move is bullish, red when it turns bearish.
  const last = candles[candles.length - 1]!;
  const prev = candles[candles.length - 2];
  const bullish = last.close >= (prev?.close ?? last.open);
  const priceColor = bullish ? candleUpColor : candleDownColor;
  const priceY = scale.yForPrice(last.close);
  if (priceY >= scale.plotTop && priceY <= scale.plotTop + scale.plotHeight) {
    ctx.save();
    ctx.strokeStyle = priceColor;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(scale.plotLeft, priceY);
    ctx.lineTo(scale.plotLeft + scale.plotWidth, priceY);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();

  // The price chip sits on the axis gutter, outside the plot clip.
  if (priceY >= scale.plotTop && priceY <= scale.plotTop + scale.plotHeight) {
    ctx.save();
    ctx.font = "9px -apple-system, 'Segoe UI', sans-serif";
    const label = last.close >= 1000 ? last.close.toFixed(1) : last.close.toFixed(2);
    ctx.fillStyle = priceColor;
    ctx.fillRect(scale.plotLeft + scale.plotWidth + 1, priceY - 8, Y_AXIS_WIDTH - 2, 16);
    ctx.fillStyle = "#fff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, scale.plotLeft + scale.plotWidth + 5, priceY);
    ctx.restore();
  }

  // Sub-pane indicators, in the bands the scale reserved for them.
  activeSubPanes(activeIndicators).forEach((kind, i) => {
    const rect = scale.subPanes[i];
    if (!rect) return;
    if (kind === "volume") drawVolumePane(ctx, candles, scale, rect);
    else if (kind === "rsi") drawRSIPane(ctx, candles, scale, rect);
    else if (kind === "stoch") drawStochPane(ctx, candles, scale, rect);
    else if (kind === "atr") drawATRPane(ctx, candles, scale, rect);
    else if (kind === "adx") drawADXPane(ctx, candles, scale, rect);
    else drawMACDPane(ctx, candles, scale, rect);
  });
  return scale;
}

/** Straight-line distance from a point to a segment — drawing hit-testing. */
export function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
